import { useState } from "react";
import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { useAuth } from "../../../components/AuthProvider";
import { createRouteError } from "../../../components/RouteErrorBoundary";
import { PageSkeleton } from "../../../components/RouteSkeleton";
import {
  useUserGamificationStats,
  useAchievements,
  useLeaderboard,
  type LeaderboardPeriod,
} from "../../../hooks/useGamificationAdmin";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { queryKeys, STALE_TIMES } from "../../../lib/queryClient";
import { getUserGamificationStats } from "../../../server/gamification";
import type { RouterContextWithSession } from "../../../router";

export const Route = createFileRoute("/_admin/gamification/")({
  beforeLoad: async ({ context }) => {
    if (typeof window === "undefined") return {};
    const sb = context.supabase;
    if (!sb) return {};
    const {
      data: { session },
    } = await sb.auth.getSession();
    if (!session) {
      throw redirect({ to: "/auth" });
    }
    return { session };
  },
  loader: async ({ context }) => {
    if (typeof window === "undefined") return;
    const { session } = context as RouterContextWithSession;
    if (!session) return;

    await context.queryClient.ensureQueryData({
      queryKey: queryKeys.gamification.stats(session.user.id),
      queryFn: async () => {
        const result = await getUserGamificationStats({
          data: { userId: session.user.id, accessToken: session.access_token },
        });
        if (result.error) throw new Error(result.error);
        return result.data;
      },
      staleTime: STALE_TIMES.aggregate,
    });
  },
  pendingComponent: () => <PageSkeleton rows={4} variant="cards" />,
  errorComponent: createRouteError("Gamification"),
  component: GamificationPage,
});

function GamificationPage() {
  const { user, session, loading, isAuthenticated } = useAuth();

  if (loading || !isAuthenticated || !user || !session) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <GamificationContent
      userId={user.id}
      accessToken={session.access_token}
    />
  );
}

const PERIODS: { value: LeaderboardPeriod; label: string }[] = [
  { value: "all_time", label: "All time" },
  { value: "month", label: "This month" },
  { value: "week", label: "This week" },
  { value: "day", label: "Today" },
];

function GamificationContent({
  userId,
  accessToken,
}: {
  userId: string;
  accessToken: string;
}) {
  const auth = { userId, accessToken };

  const {
    data: stats,
    isLoading: statsLoading,
    error: statsError,
  } = useUserGamificationStats(auth);

  const {
    data: achievements,
    isLoading: achievementsLoading,
    error: achievementsError,
  } = useAchievements(auth);

  const [period, setPeriod] = useState<LeaderboardPeriod>("all_time");
  const {
    data: leaderboard,
    isLoading: leaderboardLoading,
    error: leaderboardError,
  } = useLeaderboard(auth, { period, limit: 15 });

  return (
    <div className="text-foreground">
      <div className="container mx-auto px-4 py-6 max-w-4xl space-y-8">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Gamification</h1>
            <p className="text-muted-foreground">
              Streaks, achievements, and leaderboard
            </p>
          </div>
          <Button variant="outline" asChild>
            <Link to="/dashboard">Back to Dashboard</Link>
          </Button>
        </header>

        {/* Stats / Streaks */}
        <section>
          <Card>
            <CardHeader>
              <CardTitle>Your stats</CardTitle>
              <CardDescription>XP and streak summary</CardDescription>
            </CardHeader>
            <CardContent>
              {statsLoading && (
                <p className="text-muted-foreground">Loading stats...</p>
              )}
              {statsError && (
                <p className="text-destructive">
                  Error: {statsError.message}
                </p>
              )}
              {!statsLoading && !statsError && !stats && (
                <p className="text-muted-foreground">No stats yet.</p>
              )}
              {!statsLoading && !statsError && stats && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="rounded-lg border bg-card p-3">
                    <p className="text-xs text-muted-foreground">Total XP</p>
                    <p className="text-lg font-semibold">{stats.totalXP}</p>
                  </div>
                  <div className="rounded-lg border bg-card p-3">
                    <p className="text-xs text-muted-foreground">Today</p>
                    <p className="text-lg font-semibold">{stats.xpToday}</p>
                  </div>
                  <div className="rounded-lg border bg-card p-3">
                    <p className="text-xs text-muted-foreground">Current streak</p>
                    <p className="text-lg font-semibold">{stats.currentStreak} days</p>
                  </div>
                  <div className="rounded-lg border bg-card p-3">
                    <p className="text-xs text-muted-foreground">Longest streak</p>
                    <p className="text-lg font-semibold">{stats.longestStreak} days</p>
                  </div>
                  <div className="rounded-lg border bg-card p-3 col-span-2 sm:col-span-4">
                    <p className="text-xs text-muted-foreground">Tasks completed</p>
                    <p className="text-lg font-semibold">{stats.tasksCompleted}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </section>

        {/* Achievements */}
        <section>
          <Card>
            <CardHeader>
              <CardTitle>Achievements</CardTitle>
              <CardDescription>Badges and milestones earned</CardDescription>
            </CardHeader>
            <CardContent>
              {achievementsLoading && (
                <p className="text-muted-foreground">Loading achievements...</p>
              )}
              {achievementsError && (
                <p className="text-destructive">
                  Error: {achievementsError.message}
                </p>
              )}
              {!achievementsLoading && !achievementsError && (!achievements || achievements.length === 0) && (
                <p className="text-muted-foreground">No achievements yet. Complete tasks to earn some.</p>
              )}
              {!achievementsLoading && !achievementsError && achievements && achievements.length > 0 && (
                <ul className="space-y-2">
                  {achievements.map((a) => (
                    <li
                      key={a.id}
                      className="flex items-center justify-between rounded-lg border bg-card p-3"
                    >
                      <span className="font-medium">{a.name}</span>
                      {a.rarity && (
                        <span className="text-xs text-muted-foreground capitalize">{a.rarity}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </section>

        {/* Leaderboard */}
        <section>
          <Card>
            <CardHeader>
              <CardTitle>Leaderboard</CardTitle>
              <CardDescription>Top performers by XP</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {PERIODS.map((p) => (
                  <Button
                    key={p.value}
                    variant={period === p.value ? "default" : "outline"}
                    size="sm"
                    onClick={() => setPeriod(p.value)}
                  >
                    {p.label}
                  </Button>
                ))}
              </div>
              {leaderboardLoading && (
                <p className="text-muted-foreground">Loading leaderboard...</p>
              )}
              {leaderboardError && (
                <p className="text-destructive">
                  Error: {leaderboardError.message}
                </p>
              )}
              {!leaderboardLoading && !leaderboardError && (!leaderboard || leaderboard.length === 0) && (
                <p className="text-muted-foreground">No entries yet.</p>
              )}
              {!leaderboardLoading && !leaderboardError && leaderboard && leaderboard.length > 0 && (
                <ol className="space-y-2">
                  {leaderboard.map((entry) => (
                    <li
                      key={entry.user_id}
                      className="flex items-center justify-between rounded-lg border bg-card p-3"
                    >
                      <span className="font-medium">
                        #{entry.rank} {entry.display_name || entry.full_name || entry.username}
                      </span>
                      <span className="text-muted-foreground">
                        {entry.xp} XP · {entry.tasks_completed} tasks
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
}
